import { anySignal } from "@-/com.npmjs.any-signal";
import { withDeadline } from "#with-deadline";
import type { Deadline } from "#deadline-ms";

/** A single HTTP round trip, headers AND body, for a request with no better-known budget. */
export const FETCH_DEADLINE_MS = 20_000;

/**
 * `fetch` that cannot hang. React Native's `fetch` has no timeout at all, and a stalled connection
 * or a server that never finishes its body leaves the awaiting spinner up forever. The deadline
 * covers the whole exchange the caller awaits; the request aborts when it passes (or when the
 * caller's own signal aborts), and the caller receives a `DeadlineExceededError`.
 *
 * By default the body is read into memory under the deadline and served from a rebuilt
 * `Response`, so no later `.json()` / `.text()` / `.arrayBuffer()` can stall outside it.
 * `bufferBody: false` returns the live response for streaming consumers; the deadline then covers
 * the headers only, and the consumer must bound its own reads (a stall timer that restarts on
 * progress).
 */
export async function fetchWithDeadline(
  input: RequestInfo | URL,
  init?: RequestInit & { bufferBody?: boolean; deadline?: Deadline; operation?: string },
): Promise<Response> {
  const { bufferBody = true, deadline = FETCH_DEADLINE_MS, operation, ...requestInit } = init ?? {};
  const url =
    typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
  return withDeadline(
    { deadline, operation: operation ?? `fetch ${url.split("?")[0]}` },
    async (deadlineSignal) => {
      const signal = anySignal([requestInit.signal, deadlineSignal]);
      if (!bufferBody) {
        // The caller goes on to stream the body, and its own signal must still cancel that read.
        return fetch(input, { ...requestInit, signal });
      }
      try {
        const response = await fetch(input, { ...requestInit, signal });
        const bytes = await response.arrayBuffer();
        const hasBody = response.status !== 204 && response.status !== 304;
        // On React Native the global `Response` is whatwg-fetch, patched to decode byte bodies as
        // UTF-8 (`configs/patching/codemods/whatwg-fetch`).
        const buffered = new Response(hasBody ? bytes : null, {
          headers: response.headers,
          status: response.status,
          statusText: response.statusText,
        });
        // The constructor cannot set `url`; consumers (redirect handling, retries) read it.
        Object.defineProperty(buffered, "url", { value: response.url });
        return buffered;
      } finally {
        signal.clear();
      }
    },
  );
}
